import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatGBP, formatGBPExact } from "@/lib/avocado";
import { StatusBadge, StatusPipeline } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BRAND } from "@/lib/avocado";
import { Plus, Loader2, ChevronRight, Inbox, Share2, Copy, Check, MessageCircle, ShieldCheck } from "lucide-react";
import { BankDetailsDialog } from "@/components/BankDetailsDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function KpiCard({ label, value, sub, testId }) {
  return (
    <div className="card-lift bg-white border border-border/60 rounded-xl p-6" data-testid={testId}>
      <div className="uppercase-label">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2 tabular" style={{ color: "#1C2419" }}>{value}</div>
      {sub && <div className="text-xs mt-1.5" style={{ color: "#5C6B57" }}>{sub}</div>}
    </div>
  );
}

function NewReferralDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ street: "", town: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/referrals", form);
      toast.success("Address logged. Tom's been notified.");
      setOpen(false);
      setForm({ street: "", town: "", notes: "" });
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit referral");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-brand rounded-full h-12 px-6 font-bold flex items-center gap-2" data-testid="new-referral-button">
          <Plus size={17} strokeWidth={3} /> Log an address
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="new-referral-dialog">
        <DialogHeader>
          <p className="uppercase-label">Log a potential lead</p>
          <DialogTitle className="font-display text-3xl">New address</DialogTitle>
          <DialogDescription>
            Just the address of a home you think is up for grabs. Share your valuation link with the owner — no personal details needed from you.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg p-3 border flex items-start gap-2.5 mt-1" style={{ background: "#F7FAEF", borderColor: "#DFF1B7" }} data-testid="gdpr-notice">
          <ShieldCheck size={16} style={{ color: BRAND.greenDeep, marginTop: 2 }} />
          <p className="text-xs leading-relaxed" style={{ color: "#4C6812" }}>
            <strong>GDPR-safe:</strong> We no longer collect the homeowner's name, email or phone from you.
            Share your personal valuation link instead — the client makes contact themselves.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-3" data-testid="new-referral-form">
          <div className="space-y-1.5">
            <Label className="uppercase-label">Street</Label>
            <Input required value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })}
              placeholder="e.g. 42 Elm Avenue" data-testid="ref-street-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="uppercase-label">Town</Label>
            <Input required value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })}
              placeholder="e.g. Surbiton" data-testid="ref-town-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="uppercase-label">Notes <span className="normal-case text-[10px]" style={{ color: "#7A6D5F" }}>(optional)</span></Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any context that helps Tom — timing, why they might move, etc."
              data-testid="ref-notes-input" />
          </div>
          <button type="submit" disabled={busy} className="btn-brand w-full h-12 rounded-full font-bold text-base flex items-center justify-center disabled:opacity-70" data-testid="ref-submit-button">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Log this address"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkCard({ user }) {
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);

  useEffect(() => {
    api.get("/meta/valuation-link").then(({ data }) => setLink(data)).catch(() => setLink(null));
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const shareUrl = link?.share_url || "";
  const shareMessage = shareUrl
    ? `Hi 👋 not sure if you've been thinking about moving, but if you'd like a free, no-pressure valuation on your home, I really rate Tom at Avocado Property. Here's my referral link to book one straight in with him:\n\n${shareUrl}\n\n— ${firstName}`
    : "";

  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };
  const copyMessage = async () => {
    if (!shareMessage) return;
    try { await navigator.clipboard.writeText(shareMessage); setCopiedMsg(true); toast.success("Message copied — paste anywhere"); }
    catch { toast.error("Copy failed"); }
  };
  const openWhatsApp = () => {
    if (!shareMessage) return;
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="rounded-2xl border-2 p-6 md:p-7" style={{ background: "#FFF0F8", borderColor: "#FFAED8" }} data-testid="share-link-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Share2 size={16} style={{ color: BRAND.pink }} />
            <div className="uppercase-label" style={{ color: BRAND.pink }}>Your valuation link</div>
          </div>
          <h3 className="font-display text-2xl md:text-3xl mt-1.5 leading-tight" style={{ fontWeight: 700 }}>
            Share this <span className="italic">once</span>, get paid every time it lands.
          </h3>
          <p className="text-sm mt-2" style={{ color: "#7A6D5F" }}>
            When someone books a valuation through your link, they're tagged to you automatically. No awkward name-collecting — the homeowner reaches out themselves.
          </p>
        </div>
        {link?.referral_code && (
          <div className="text-right shrink-0">
            <div className="uppercase-label" style={{ color: BRAND.pink }}>Your code</div>
            <div className="font-display text-2xl tabular font-bold mt-0.5" style={{ color: BRAND.pink }} data-testid="referral-code">
              {link.referral_code}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2 bg-white rounded-lg border p-2" style={{ borderColor: "#FFAED8" }}>
        <input
          readOnly
          value={shareUrl}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 text-xs md:text-sm tabular bg-transparent outline-none px-2"
          data-testid="share-link-input"
        />
        <Button size="sm" onClick={copyLink} disabled={!shareUrl} className="btn-brand rounded-full gap-1 h-9 px-3 shrink-0" data-testid="share-link-copy">
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy link"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={openWhatsApp} disabled={!shareUrl} className="rounded-full gap-2 h-11 px-5 font-bold text-white" style={{ background: "#25D366" }} data-testid="share-whatsapp-button">
          <MessageCircle size={16} /> Share on WhatsApp
        </Button>
        <Button variant="ghost" onClick={copyMessage} disabled={!shareUrl} className="rounded-full gap-2 h-11 px-5" data-testid="share-copy-message-button">
          {copiedMsg ? <Check size={14} /> : <Copy size={14} />} Copy pre-written message
        </Button>
      </div>
    </div>
  );
}

function ReferralCard({ r, onClick }) {
  const primary = r.property_address || r.client_name || "New address";
  const secondary = r.client_name && r.client_name !== primary ? `Client: ${r.client_name}` : (r.notes ? r.notes.slice(0, 60) : "");
  return (
    <button
      onClick={onClick}
      className={`text-left w-full card-lift bg-white border border-border/60 rounded-xl p-5 flex items-start gap-4 ${r.removed ? "opacity-70" : ""}`}
      data-testid={`referral-card-${r.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          {r.removed ? <StatusBadge status="Removed" /> : <StatusBadge status={r.status} />}
          {r.removed && (
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#991B1B" }} data-testid="deleted-tag">Deleted</span>
          )}
          <span className="text-[11px]" style={{ color: "#5C6B57" }}>
            {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </span>
        </div>
        <div className={`font-display text-lg leading-tight truncate ${r.removed ? "line-through" : ""}`} style={{ color: "#1C2419" }}>{primary}</div>
        {secondary && <div className="text-sm mt-0.5 truncate" style={{ color: "#5C6B57" }}>{secondary}</div>}
        {r.removed && r.removed_reason && (
          <div className="text-xs mt-1 italic truncate" style={{ color: "#B91C1C" }}>Reason: {r.removed_reason}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="uppercase-label">Your fee</div>
        <div className="font-display text-2xl tabular font-bold" style={{ color: r.removed ? "#9CA396" : BRAND.pink }}>
          {r.removed ? "£0" : formatGBP(r.referral_value)}
        </div>
      </div>
      <ChevronRight size={18} className="text-muted-foreground self-center" />
    </button>
  );
}

function ReferralDetailDialog({ referral, open, onClose }) {
  if (!referral) return null;
  const heading = referral.property_address || referral.client_name || "Referral";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="referral-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">{referral.removed ? <StatusBadge status="Removed" /> : <StatusBadge status={referral.status} />}</div>
          <DialogTitle className="font-display text-3xl">{heading}</DialogTitle>
          {referral.client_name && <DialogDescription>Client: {referral.client_name}</DialogDescription>}
        </DialogHeader>

        {referral.removed ? (
          <div className="rounded-2xl p-5 border-2" style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}>
            <div className="uppercase-label" style={{ color: "#991B1B" }}>Not proceeding</div>
            <p className="mt-2 text-sm" style={{ color: "#7F1D1D" }}>{referral.removed_reason || "This client decided not to proceed with Avocado Property."}</p>
            <p className="text-xs mt-3" style={{ color: "#7A6D5F" }}>
              No fee will be paid on this referral. Thanks for the introduction all the same — send us the next one!
            </p>
          </div>
        ) : (
          <div className="rounded-2xl p-6 border-2" style={{ background: "#FFF0F8", borderColor: "#FFAED8" }}>
            <div className="uppercase-label" style={{ color: BRAND.pink }}>Your referral value (5%)</div>
            <div className="font-display text-5xl md:text-6xl mt-2 tabular font-bold" style={{ color: BRAND.pink }}>
              {formatGBPExact(referral.referral_value)}
            </div>
            <p className="text-sm mt-3" style={{ color: "#7A6D5F" }}>
              Updates automatically as Tom moves the deal forward. Paid the moment Tom receives full payment soon after legal completion.
            </p>
          </div>
        )}

        {!referral.removed && (
          <div className="mt-2">
            <div className="uppercase-label mb-3">Pipeline progress</div>
            <StatusPipeline current={referral.status} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-sm">
          {referral.notes && <div className="md:col-span-2"><div className="uppercase-label">Notes</div><div className="whitespace-pre-wrap">{referral.notes}</div></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReferrerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "Referred" | "Confirmed Client" | "Sale Completed" | "deleted"

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/referrals/mine"), api.get("/referrals/mine/stats")]);
      setReferrals(r.data); setStats(s.data);
    } catch {
      toast.error("Could not load your dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  const firstName = useMemo(() => (user?.name || "").split(" ")[0] || "there", [user]);

  const filteredReferrals = useMemo(() => {
    return referrals.filter((r) => {
      if (statusFilter === "active") {
        if (r.removed) return false;
        return r.status === "Referred" || r.status === "Confirmed Client";
      }
      if (statusFilter === "deleted") return !!r.removed;
      // specific stage
      if (r.removed) return false;
      return r.status === statusFilter;
    });
  }, [referrals, statusFilter]);

  const counts = useMemo(() => {
    const c = { active: 0, "Referred": 0, "Confirmed Client": 0, "Sale Completed": 0, deleted: 0 };
    for (const r of referrals) {
      if (r.removed) { c.deleted += 1; continue; }
      if (r.status === "Referred" || r.status === "Confirmed Client") c.active += 1;
      if (c[r.status] !== undefined) c[r.status] += 1;
    }
    return c;
  }, [referrals]);

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="uppercase-label" style={{ color: BRAND.pink }}>Your dashboard</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-1 leading-[1.02]" style={{ fontWeight: 700 }}>
            Alright, <span className="italic">{firstName}.</span>
          </h1>
          <p className="text-sm md:text-base mt-3 max-w-xl" style={{ color: "#7A6D5F" }}>
            Share your valuation link, log addresses you're eyeing up, and track every deal Tom closes on your behalf. <strong>5% of his commission, every completed sale.</strong>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BankDetailsDialog />
          <NewReferralDialog onCreated={load} />
        </div>
      </div>

      {/* Share valuation link — primary CTA now */}
      <div className="mb-10">
        <ShareLinkCard user={user} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KpiCard
          label="Active referrals"
          value={stats?.total_referrals ?? "—"}
          sub={stats?.removed_count ? `+${stats.removed_count} deleted` : "Not counting deleted"}
          testId="kpi-total-referrals"
        />
        <KpiCard label="In the pipeline" value={stats?.active_referrals ?? "—"} sub="Referred + Confirmed" testId="kpi-active-deals" />
        <KpiCard label="Pipeline value" value={formatGBP(stats?.pipeline_value ?? 0)} sub="Estimated, in progress" testId="kpi-pipeline-value" />
        <KpiCard label="Paid to you" value={formatGBP(stats?.paid_value ?? 0)} sub="Completed & settled" testId="kpi-paid-value" />
      </div>

      <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl md:text-3xl">Your referrals</h2>
          <span className="text-xs" style={{ color: "#5C6B57" }} data-testid="referrals-count-label">
            Showing {filteredReferrals.length} of {referrals.length} · {statusFilter === "active" ? "Active only (Referred + Confirmed Client)" : statusFilter === "deleted" ? "Deleted only" : statusFilter}
          </span>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-64 h-11 bg-white" data-testid="referrer-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid="referrer-filter-active">Active ({counts.active})</SelectItem>
            <SelectItem value="Referred" data-testid="referrer-filter-referred">Referred ({counts["Referred"]})</SelectItem>
            <SelectItem value="Confirmed Client" data-testid="referrer-filter-confirmed">Confirmed Client ({counts["Confirmed Client"]})</SelectItem>
            <SelectItem value="Sale Completed" data-testid="referrer-filter-completed">Sale Completed ({counts["Sale Completed"]})</SelectItem>
            <SelectItem value="deleted" data-testid="referrer-filter-deleted">Deleted ({counts.deleted})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin" size={24} style={{ color: BRAND.pink }} /></div>
      ) : referrals.length === 0 ? (
        <div className="bg-dots border-2 border-dashed rounded-2xl p-12 text-center" style={{ borderColor: "#FFAED8" }} data-testid="empty-state">
          <Inbox size={32} className="mx-auto mb-3" style={{ color: BRAND.pink }} />
          <h3 className="font-display text-2xl mb-1" style={{ fontWeight: 700 }}>Nothing here yet</h3>
          <p className="text-sm mb-5" style={{ color: "#7A6D5F" }}>Share your valuation link above, or log an address you've spotted.</p>
          <NewReferralDialog onCreated={load} />
        </div>
      ) : filteredReferrals.length === 0 ? (
        <div className="bg-white border border-dashed rounded-2xl p-10 text-center" style={{ borderColor: "#E4DBCB" }} data-testid="empty-filter-state">
          <p className="text-sm" style={{ color: "#7A6D5F" }}>Nothing matches this filter. Try another option or reset to <button className="underline font-semibold" style={{ color: BRAND.pink }} onClick={() => setStatusFilter("active")}>Active</button>.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="referrals-list">
          {filteredReferrals.map((r) => (
            <ReferralCard key={r.id} r={r} onClick={() => setSelected(r)} />
          ))}
        </div>
      )}

      <ReferralDetailDialog referral={selected} open={!!selected} onClose={() => setSelected(null)} />
    </AppShell>
  );
}
